/**
 * Appointment Package Selection JavaScript
 * Handles Step 2: Package selection functionality
 *
 * IMPORTANT: When modifying this file, update APPOINTMENT_SCHEDULING_FEATURE.md
 */

// Global variable to store packages product data
let packagesProductData = null;

// Load packages product data from the embedded JSON
function loadPackagesProductData() {
    try {
        const productDataScript = document.getElementById('packages-product-data');
        if (productDataScript) {
            packagesProductData = JSON.parse(productDataScript.textContent);
            return packagesProductData;
        } else {
            return null;
        }
    } catch (error) {
        return null;
    }
}

// Get packages product data (loads if not already loaded)
function getPackagesProductData() {
    if (!packagesProductData) {
        packagesProductData = loadPackagesProductData();
        // Also expose to window object for global access
        if (packagesProductData) {
            window.packagesProductData = packagesProductData;
        }
    }
    return packagesProductData;
}

// Get price for a specific package type (Non-Member pricing)
function getPackagePrice(packageType) {
    const productData = getPackagesProductData();
    if (!productData || !productData.variants) {
        return null;
    }

    // Map package types to enhancement option values
    const enhancementMap = {
        'signature': 'None',
        'silver': 'Silver',
        'gold': 'Gold',
        'platinum': 'Platinum'
    };

    const enhancement = enhancementMap[packageType.toLowerCase()];
    if (!enhancement) {
        return null;
    }

    // Find the Non-Member variant with the matching enhancement
    const variant = productData.variants.find(v =>
        v.option1 === 'Non-Member' && v.option2 === enhancement
    );

    if (variant) {
        // Handle price from Shopify - prices come in cents, need to convert to dollars
        // Parse price and convert from cents to dollars
        let priceStr = String(variant.price);
        let priceInCents = parseFloat(priceStr);
        let price = priceInCents / 100; // Convert cents to dollars

        return price;
    } else {
        return null;
    }
}

// Get formatted price string for display
function getFormattedPackagePrice(packageType) {
    const price = getPackagePrice(packageType);

    if (price !== null && !isNaN(price)) {
        // If price is a whole number, show without decimals, otherwise show with decimals
        if (price % 1 === 0) {
            const formatted = price.toString();
            return formatted; // e.g. "285"
        } else {
            const formatted = price.toFixed(2);
            return formatted; // e.g. "285.50"
        }
    }
    return '0'; // Fallback
}

// Format a numeric amount (in dollars) for display without currency symbol
function formatDollarAmount(amount) {
    if (amount === null || typeof amount === 'undefined' || isNaN(amount)) {
        return '0';
    }
    return amount % 1 === 0 ? amount.toString() : amount.toFixed(2);
}

// Update all package prices in the DOM
function updatePackagePrices() {
    const packageTypes = ['signature', 'silver', 'gold', 'platinum'];

    packageTypes.forEach(packageType => {
        const priceNumber = getPackagePrice(packageType);
        const price = formatDollarAmount(priceNumber);

        // Update desktop version
        const desktopPriceElement = document.querySelector(`.package-column-${packageType} .package-price`);
        if (desktopPriceElement) {
            desktopPriceElement.innerHTML = `<span class="currency">$</span>${price}`;
        }

        // Update mobile version (if it exists)
        const mobilePriceElement = document.querySelector(`[data-package="${packageType}"] .package-price`);
        if (mobilePriceElement && mobilePriceElement !== desktopPriceElement) {
            mobilePriceElement.innerHTML = `<span class="currency">$</span>${price}`;
        }

        // Update original price (price minus $19 physician fee) in all matching contexts
        const originalAmount = Math.max(0, (priceNumber || 0) - 19);
        const originalFormatted = formatDollarAmount(originalAmount);
        const originalPriceNodes = document.querySelectorAll(`.package-column-${packageType} .original-price-amount`);
        originalPriceNodes.forEach(node => {
            node.textContent = originalFormatted;
        });
    });
}

// Expose functions globally for external access
window.AppointmentPackages = {
    getPackagesProductData,
    getPackagePrice,
    getFormattedPackagePrice,
    updatePackagePrices,
    clearGenderSelection
};

// Expose gender functions globally
window.loadSavedGenderSelection = loadSavedGenderSelection;
window.showGenderPreSelection = showGenderPreSelection;


// Show package selection section with animation
function showPackageSelectionSection() {
    const packageSection = document.querySelector('.package-selection-section');

    if (packageSection) {
        // Ensure prices are updated before showing
        updatePackagePrices();

        // Use setTimeout to ensure smooth animation
        setTimeout(() => {
            packageSection.classList.add('visible');
        }, 100);
    }
}





// Hide package selection section
function hidePackageSelectionSection() {
    const packageSection = document.querySelector('.package-selection-section');
    if (packageSection) {
        packageSection.classList.remove('visible');
    }
}


// Display selected appointment summary
function displayAppointmentSummary() {
    const summaryContainer = document.getElementById('selected-appointment-summary');
    const summaryContent = document.querySelector('.appointment-summary-content');

    if (!summaryContainer || !summaryContent) {
        return;
    }

    const appointmentData = loadSelectedAppointmentFromSession();
    if (appointmentData) {
        const { date, time, location } = appointmentData;

        // Create the summary sentence (without warning - that will be added by checkAndShowRestrictedStateWarning)
        const summaryMessage = `Screening: ${date} at ${time} at ${location}.`;

        // Update the text and show the container
        summaryContent.textContent = summaryMessage;
        summaryContainer.style.display = 'block';

        // Setup modify button functionality
        setupAppointmentModifyButton();
    } else {
        // Hide the container if no appointment data
        summaryContainer.style.display = 'none';
    }
}

// Setup appointment modify button functionality
function setupAppointmentModifyButton() {
    const modifyBtn = document.getElementById('appointment-modify-btn');

    if (modifyBtn) {
        // Remove existing event listeners
        modifyBtn.replaceWith(modifyBtn.cloneNode(true));
        const newModifyBtn = document.getElementById('appointment-modify-btn');

        newModifyBtn.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();

            // Navigate back to appointment selection
            if (typeof showAppointmentSelection === 'function') {
                showAppointmentSelection();
            }
        });

        // Add keyboard support
        newModifyBtn.addEventListener('keydown', function(e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (typeof showAppointmentSelection === 'function') {
                    showAppointmentSelection();
                }
            }
        });


    }
}

// Initialize accordion functionality
function initializeAccordions() {
    // Remove any existing event listeners to prevent duplicates
    document.removeEventListener('click', handleAccordionClick);
    document.removeEventListener('keydown', handleAccordionKeydown);

    // Use event delegation to handle both original and cloned accordions
    document.addEventListener('click', handleAccordionClick);
    document.addEventListener('keydown', handleAccordionKeydown);

    // Set default states based on screen size
    setAccordionDefaultStates();
}

// Handle accordion click events
function handleAccordionClick(e) {
    const header = e.target.closest('.accordion-header');
    if (header) {
        e.preventDefault();
        toggleAccordion(header);
    }
}

// Handle accordion keyboard events
function handleAccordionKeydown(e) {
    const header = e.target.closest('.accordion-header');
    if (header && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        toggleAccordion(header);
    }
}

// Set default accordion states based on screen size
function setAccordionDefaultStates() {
    const isMobile = window.innerWidth <= 990;
    // Select accordion elements from both desktop and mobile swiper
    const accordionHeaders = document.querySelectorAll('.accordion-header');
    const accordionContents = document.querySelectorAll('.accordion-content');

    if (!isMobile) {
        // Desktop: Open all accordions by default
        accordionHeaders.forEach(header => {
            header.setAttribute('aria-expanded', 'true');
            header.classList.add('expanded');
        });

        accordionContents.forEach(content => {
            content.setAttribute('aria-hidden', 'false');
        });
    } else {
        // Mobile: Close all accordions by default
        accordionHeaders.forEach(header => {
            header.setAttribute('aria-expanded', 'false');
            header.classList.remove('expanded');
        });

        accordionContents.forEach(content => {
            content.setAttribute('aria-hidden', 'true');
        });
    }
}

// Toggle accordion state with cross-package syncing
function toggleAccordion(header) {
    const isExpanded = header.getAttribute('aria-expanded') === 'true';
    const content = header.nextElementSibling;
    const accordionType = getAccordionType(header);

    // Toggle the clicked accordion
    if (isExpanded) {
        header.setAttribute('aria-expanded', 'false');
        header.classList.remove('expanded');
        content.setAttribute('aria-hidden', 'true');

        // Sync with other packages - close the same accordion type in other packages
        syncAccordionAcrossPackages(accordionType, false);
    } else {
        header.setAttribute('aria-expanded', 'true');
        header.classList.add('expanded');
        content.setAttribute('aria-hidden', 'false');

        // Sync with other packages - open the same accordion type in other packages
        syncAccordionAcrossPackages(accordionType, true);
    }
}

// Get the screening ID from the accordion header
function getAccordionType(header) {
    return header.getAttribute('data-screening-id') || '';
}

// Sync accordion state across all packages
function syncAccordionAcrossPackages(accordionType, shouldOpen) {
    if (!accordionType) return;

    const allHeaders = document.querySelectorAll('.accordion-header');

    allHeaders.forEach((header) => {
        const headerScreeningId = header.getAttribute('data-screening-id');
        if (headerScreeningId === accordionType) {
            const content = header.nextElementSibling;
            const isCurrentlyExpanded = header.getAttribute('aria-expanded') === 'true';

            // Only sync if the state is different (to avoid unnecessary DOM changes)
            if (shouldOpen && !isCurrentlyExpanded) {
                header.setAttribute('aria-expanded', 'true');
                header.classList.add('expanded');
                if (content) {
                    content.setAttribute('aria-hidden', 'false');
                }
            } else if (!shouldOpen && isCurrentlyExpanded) {
                header.setAttribute('aria-expanded', 'false');
                header.classList.remove('expanded');
                if (content) {
                    content.setAttribute('aria-hidden', 'true');
                }
            }
        }
    });
}

// Handle window resize to update default states
function handleWindowResize() {
    setAccordionDefaultStates();

    // Handle swiper initialization on resize
    if (window.innerWidth <= 990) {
        // Initialize swiper if not already initialized
        if (!document.querySelector('.package-swiper').swiper) {
            initializePackageSwiper();
        }
    }
}

// Populate mobile swiper slides with package content
function populateMobileSwiperSlides() {
    const swiperWrapper = document.getElementById('package-swiper-wrapper');
    const desktopPackages = document.querySelectorAll('.package-column');

    if (!swiperWrapper || !desktopPackages.length) {
        return;
    }

    // Clear existing slides
    swiperWrapper.innerHTML = '';

    // Clone desktop packages into swiper slides
    desktopPackages.forEach((packageColumn, index) => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';

        // Clone the package column content
        const clonedPackage = packageColumn.cloneNode(true);
        slide.appendChild(clonedPackage);

        swiperWrapper.appendChild(slide);
    });

    // Reinitialize accordion states for cloned elements after a short delay
    setTimeout(() => {
        setAccordionDefaultStates();
    }, 100);
}

// Initialize Swiper for mobile package slider
function initializePackageSwiper() {
    // Check if Swiper is available and we're on mobile
    if (typeof Swiper !== 'undefined' && window.innerWidth <= 990) {
        const swiperContainer = document.querySelector('.package-swiper');
        if (swiperContainer) {
            // Populate slides first
            populateMobileSwiperSlides();

            const packageSwiper = new Swiper('.package-swiper', {
                slidesPerView: 1.2,
                spaceBetween: 8,
                centeredSlides: false,
                pagination: {
                    el: '.swiper-pagination',
                    clickable: true,
                },
                navigation: {
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                },
                breakpoints: {
                    480: {
                        slidesPerView: 2.3,
                        spaceBetween: 12,
                        centeredSlides: false,
                    },
                    640: {
                        slidesPerView: 3.3,
                        spaceBetween: 16,
                        centeredSlides: false,
                    },
                }
            });

            // Store swiper instance for later use
            swiperContainer.swiper = packageSwiper;
        }
    }
}

// Utility function to close all accordions
function closeAllAccordions() {
    const accordionHeaders = document.querySelectorAll('.accordion-header');
    const accordionContents = document.querySelectorAll('.accordion-content');

    accordionHeaders.forEach(header => {
        header.setAttribute('aria-expanded', 'false');
        header.classList.remove('expanded');
    });

    accordionContents.forEach(content => {
        content.setAttribute('aria-hidden', 'true');
    });
}

// Utility function to open all accordions
function openAllAccordions() {
    const accordionHeaders = document.querySelectorAll('.accordion-header');
    const accordionContents = document.querySelectorAll('.accordion-content');

    accordionHeaders.forEach(header => {
        header.setAttribute('aria-expanded', 'true');
        header.classList.add('expanded');
    });

    accordionContents.forEach(content => {
        content.setAttribute('aria-hidden', 'false');
    });
}

// Setup package selection functionality
function setupPackageSelection() {
    // Remove any existing event listeners to prevent duplicates
    document.removeEventListener('click', handlePackageButtonClick);
    document.removeEventListener('keydown', handlePackageButtonKeydown);

    // Use event delegation to handle both original and cloned buttons
    document.addEventListener('click', handlePackageButtonClick);
    document.addEventListener('keydown', handlePackageButtonKeydown);
}

// Handle package button clicks
function handlePackageButtonClick(e) {
    const button = e.target.closest('.package-select-btn');
    if (button) {
        e.preventDefault();
        const packageType = button.getAttribute('data-package');
        selectPackage(packageType);
    }
}

// Handle package button keyboard events
function handlePackageButtonKeydown(e) {
    const button = e.target.closest('.package-select-btn');
    if (button && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        const packageType = button.getAttribute('data-package');
        selectPackage(packageType);
    }
}

// Load saved gender selection from session storage
function loadSavedGenderSelection() {
    const savedGender = loadFromSession('selectedGender', 'genderTimestamp');

    if (savedGender) {

        // Auto-advance to package selection if gender was previously selected
        const preSelectionContainer = document.querySelector('.gender-pre-selection-container');
        const packageSection = document.querySelector('.package-selection-section');
        const genderToggleOptions = document.querySelectorAll('.gender-option');

        if (preSelectionContainer) {
            preSelectionContainer.classList.add('hidden');
        }

        if (packageSection) {
            packageSection.classList.add('active');
            packageSection.setAttribute('data-selected-gender', savedGender);
        }

        // Update gender toggle to reflect saved selection
        genderToggleOptions.forEach(option => {
            option.classList.remove('active');
            if (option.getAttribute('data-gender') === savedGender) {
                option.classList.add('active');
            }
        });
    }
}

// Setup gender pre-selection functionality
function setupGenderPreSelection() {
    const preSelectionButtons = document.querySelectorAll('.gender-pre-selection');
    const preSelectionContainer = document.querySelector('.gender-pre-selection-container');
    const packageSection = document.querySelector('.package-selection-section');
    const genderToggleOptions = document.querySelectorAll('.gender-option');

    preSelectionButtons.forEach(button => {
        button.addEventListener('click', function() {
            const selectedGender = this.getAttribute('data-gender');

            // Store selected gender in session storage
            saveToSession('selectedGender', selectedGender, 'genderTimestamp');

            // Hide pre-selection container with animation
            if (preSelectionContainer) {
                // First add hiding class for animation
                preSelectionContainer.classList.add('hiding');

                // After animation completes, hide completely
                setTimeout(() => {
                    preSelectionContainer.classList.add('hidden');
                }, 400);
            }

            // Check if DOB is enabled before showing DOB step
            setTimeout(() => {
                const dobEnabled = checkIfDobEnabled();

                if (dobEnabled && typeof showDateOfBirthStep === 'function') {
                    showDateOfBirthStep();
                } else {
                    // Skip DOB step - go directly to package selection
                    if (packageSection) {
                        packageSection.classList.add('active');
                        packageSection.setAttribute('data-selected-gender', selectedGender);
                    }
                }
            }, 200);

            // Update gender toggle to reflect the selection
            genderToggleOptions.forEach(option => {
                option.classList.remove('active');
                if (option.getAttribute('data-gender') === selectedGender) {
                    option.classList.add('active');
                }
            });

        });
    });
}

// Function to clear gender selection
function clearGenderSelection() {
    // Clear from session storage
    clearFromSession('selectedGender', 'genderTimestamp');

    // Reset UI to initial state
    const preSelectionContainer = document.querySelector('.gender-pre-selection-container');
    const packageSection = document.querySelector('.package-selection-section');
    const genderToggleOptions = document.querySelectorAll('.gender-option');


    if (preSelectionContainer) {
        preSelectionContainer.classList.remove('hidden');
        preSelectionContainer.classList.remove('hiding');
    }

    if (packageSection) {
        packageSection.classList.remove('active');
        packageSection.removeAttribute('data-selected-gender');
    }

    // Reset gender toggle to default (female)
    genderToggleOptions.forEach(option => {
        option.classList.remove('active');
        if (option.getAttribute('data-gender') === 'female') {
            option.classList.add('active');
        }
    });

}

// Function to show gender pre-selection again (if needed)
function showGenderPreSelection() {
    const preSelectionContainer = document.querySelector('.gender-pre-selection-container');
    const packageSection = document.querySelector('.package-selection-section');

    if (preSelectionContainer) {
        preSelectionContainer.classList.remove('hidden');
        preSelectionContainer.classList.remove('hiding');
    }

    if (packageSection) {
        packageSection.classList.remove('active');
    }
}

// Setup gender toggle functionality
function setupGenderToggle() {
    const genderOptions = document.querySelectorAll('.gender-option');

    genderOptions.forEach(option => {
        option.addEventListener('click', function() {
            // Remove active class from all options
            genderOptions.forEach(opt => opt.classList.remove('active'));

            // Add active class to clicked option
            this.classList.add('active');

            // Get selected gender
            const selectedGender = this.getAttribute('data-gender');


            // Store selected gender in session storage
            saveToSession('selectedGender', selectedGender, 'genderTimestamp');

            // Update package section data attribute for filtering
            const packageSection = document.querySelector('.package-selection-section');
            if (packageSection) {
                packageSection.setAttribute('data-selected-gender', selectedGender);
            }

        });
    });
}

// Setup close button for appointment summary
function setupAppointmentSummaryCloseButton() {
    const closeButton = document.getElementById('appointment-summary-close');

    if (closeButton) {
        closeButton.addEventListener('click', function(e) {
            e.preventDefault();
            hideAppointmentSummary();
        });

        // Add keyboard support
        closeButton.addEventListener('keydown', function(e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                hideAppointmentSummary();
            }
        });
    }
}

// Hide appointment summary
function hideAppointmentSummary() {
    const summaryContainer = document.getElementById('selected-appointment-summary');

    if (summaryContainer) {
        summaryContainer.style.display = 'none';
    }
}

// Handle package selection
function selectPackage(packageType, autoTransition = true) {
    // Remove selected class from all columns
    const allColumns = document.querySelectorAll('.package-column');
    allColumns.forEach(column => {
        column.classList.remove('selected');
        const button = column.querySelector('.package-select-btn');
        if (button) {
            button.setAttribute('aria-selected', 'false');
            button.textContent = 'Select Package';
        }
    });

    // Add selected class to clicked package column
    const selectedColumn = document.querySelector(`[data-package="${packageType}"]`).closest('.package-column');
    if (selectedColumn) {
        selectedColumn.classList.add('selected');
        const button = selectedColumn.querySelector('.package-select-btn');
        if (button) {
            button.setAttribute('aria-selected', 'true');
            button.textContent = 'Selected';
        }
    }

    // Save selected package to session storage
    const packageData = {
        type: packageType,
        timestamp: Date.now()
    };

    saveToSession('selectedPackage', packageData, 'packageTimestamp');

    // Store package selection (legacy support)
    if (typeof selectedAppointmentData !== 'undefined' && selectedAppointmentData) {
        selectedAppointmentData.enhancement = packageType;

        // Save to session storage
        if (typeof saveSelectedAppointmentToSession === 'function') {
            saveSelectedAppointmentToSession(selectedAppointmentData);
        }

    }

    // Check if this is a restricted state
    const isRestrictedState = checkIfRestrictedState();

    // Transition logic based on restricted state
    if (autoTransition) {
        setTimeout(() => {
            if (isRestrictedState) {
                // In restricted states, skip membership step and go directly to cart with Non-Member option
                if (typeof addToCartWithMembership === 'function') {
                    addToCartWithMembership(false); // false = Non-Member
                }
            } else {
                // Normal flow - go to membership step
                if (typeof showMembershipStep === 'function') {
                    showMembershipStep();
                }
            }
        }, 500);
    }
}

// Restore package selection from session data
function restorePackageSelection() {
    const appointmentData = loadSelectedAppointmentFromSession();

    if (appointmentData && appointmentData.enhancement) {
        const packageType = appointmentData.enhancement;
        selectPackage(packageType, false); // Don't auto-transition when restoring
    }
}

// Initialize package selection step
function initializePackageSelection() {
    // Load product data and update prices
    getPackagesProductData();
    updatePackagePrices();

    // Display the selected appointment summary
    displayAppointmentSummary();

    // Check and show restricted state warning if applicable (after summary is set)
    checkAndShowRestrictedStateWarning();

    // Initialize accordion functionality
    initializeAccordions();

    // Setup package selection functionality
    setupPackageSelection();

    // Don't auto-load gender selection here - it will be loaded when package selection is shown

    // Setup gender pre-selection functionality
    setupGenderPreSelection();

    // Setup gender toggle functionality
    setupGenderToggle();

    // Initialize mobile swiper if on mobile
    if (window.innerWidth <= 990) {
        initializePackageSwiper();
    }

    // Add window resize listener for responsive accordion behavior
    window.addEventListener('resize', handleWindowResize);

    // Setup close button for appointment summary
    setupAppointmentSummaryCloseButton();

    // Show package selection section
    showPackageSelectionSection();

    // Restore package selection if available
    setTimeout(() => {
        restorePackageSelection();
    }, 100);

}

// Clear package selection data
function clearPackageSelectionData() {
    // Remove enhancement selection from appointment data
    const appointmentData = loadSelectedAppointmentFromSession();
    if (appointmentData) {
        delete appointmentData.enhancement;
        saveSelectedAppointmentToSession(appointmentData);
    }

    // Clear visual selection for package columns
    const packageColumns = document.querySelectorAll('.package-column');
    packageColumns.forEach(column => {
        column.classList.remove('selected');
        const button = column.querySelector('.package-select-btn');
        if (button) {
            button.setAttribute('aria-selected', 'false');
            button.textContent = 'Select Package';
        }
    });

    // Set mobile default state for accordions (closed)
    const accordionHeaders = document.querySelectorAll('.accordion-header');
    const accordionContents = document.querySelectorAll('.accordion-content');

    accordionHeaders.forEach(header => {
        header.setAttribute('aria-expanded', 'false');
        header.classList.remove('expanded');
    });

    accordionContents.forEach(content => {
        content.setAttribute('aria-hidden', 'true');
    });

    // Hide package selection section
    hidePackageSelectionSection();

    // Remove window resize listener
    window.removeEventListener('resize', handleWindowResize);
}

// Check if selected appointment is in a restricted state and update summary
function checkAndShowRestrictedStateWarning() {
    const summaryContainer = document.getElementById('selected-appointment-summary');
    const summaryContent = document.querySelector('.appointment-summary-content');

        if (!summaryContainer || !summaryContent) {
            return;
        }

    // Get appointment data from session storage
    const appointmentData = loadSelectedAppointmentFromSession();

    // Check if we have selected appointment data and if it's in a restricted state
    if (appointmentData &&
        appointmentData.screening &&
        appointmentData.screening.state &&
        window.RESTRICTED_STATES &&
        window.RESTRICTED_STATES.includes(appointmentData.screening.state)) {

        // Update the summary content to include the warning
        const { date, time, location } = appointmentData;
        const summaryMessage = `Screening: ${date} at ${time} at ${location}.`;
        summaryContent.textContent = summaryMessage;

        // Show the summary container
        summaryContainer.style.display = 'block';

        // Add warning styling to the summary
        summaryContainer.classList.add('has-restricted-warning');

        // Hide package selection title when in restricted state (only one option)
        hidePackageSelectionTitle();

        // Hide non-signature packages and show only Signature package
        hideNonSignaturePackages();

        // Hide progress step 3 when in restricted state
        hideProgressStep3();
    } else {
        // Remove warning styling if not in a restricted state
        summaryContainer.classList.remove('has-restricted-warning');

        // Show package selection title when not in restricted state
        showPackageSelectionTitle();

        // Show all packages when not in restricted state
        showAllPackages();

        // Show progress step 3 when not in restricted state
        showProgressStep3();
    }
}

// Hide package selection title when in restricted state
function hidePackageSelectionTitle() {
    const titleElement = document.getElementById('package-selection-select-screening-package');
    if (titleElement) {
        titleElement.style.display = 'none';
    }
}

// Show package selection title when not in restricted state
function showPackageSelectionTitle() {
    const titleElement = document.getElementById('package-selection-select-screening-package');
    if (titleElement) {
        titleElement.style.display = '';
    }
}

// Hide non-signature packages when in restricted state
function hideNonSignaturePackages() {
    const packageColumns = document.querySelectorAll('.package-column');
    const packageCardsContainer = document.querySelector('.package-cards-container');

    // Add restricted state class to container
    if (packageCardsContainer) {
        packageCardsContainer.classList.add('restricted-state');
    }

    packageColumns.forEach(column => {
        // Check if this is not a signature package
        if (!column.classList.contains('package-column-signature')) {
            column.style.display = 'none';
        }
    });

    // Also hide in mobile swiper
    const swiperWrapper = document.getElementById('package-swiper-wrapper');
    if (swiperWrapper) {
        const swiperSlides = swiperWrapper.querySelectorAll('.swiper-slide');
        swiperSlides.forEach(slide => {
            if (!slide.querySelector('.package-column-signature')) {
                slide.style.display = 'none';
            }
        });
    }

    // Hide accordion items that are not included in Signature package
    hideNonSignatureAccordionItems();

    // Hide mobile swiper when in restricted state (only one package)
    hideMobileSwiper();
}

// Show all packages when not in restricted state
function showAllPackages() {
    const packageColumns = document.querySelectorAll('.package-column');
    const packageCardsContainer = document.querySelector('.package-cards-container');

    // Remove restricted state class from container
    if (packageCardsContainer) {
        packageCardsContainer.classList.remove('restricted-state');
    }

    packageColumns.forEach(column => {
        column.style.display = '';
    });

    // Also show in mobile swiper
    const swiperWrapper = document.getElementById('package-swiper-wrapper');
    if (swiperWrapper) {
        const swiperSlides = swiperWrapper.querySelectorAll('.swiper-slide');
        swiperSlides.forEach(slide => {
            slide.style.display = '';
        });
    }

    // Show all accordion items
    showAllAccordionItems();

    // Show package headers when not in restricted state
    showPackageHeaders();

    // Show mobile swiper when not in restricted state
    showMobileSwiper();
}

// Hide accordion items that are not included in Signature package
function hideNonSignatureAccordionItems() {
    // Find all accordion items that are not included in Signature package
    const accordionItems = document.querySelectorAll('.accordion-item');

    accordionItems.forEach(item => {
        // Check if this accordion item is included in Signature package
        // Only show items that have the 'included' class within the signature package column
        const isInSignatureColumn = item.closest('.package-column-signature');
        const isIncluded = item.classList.contains('included');

        if (!isInSignatureColumn || !isIncluded) {
            item.style.display = 'none';
        }
    });
}

// Show all accordion items
function showAllAccordionItems() {
    const accordionItems = document.querySelectorAll('.accordion-item');

    accordionItems.forEach(item => {
        item.style.display = '';
    });
}

// Show package headers when not in restricted state
function showPackageHeaders() {
    const packageHeaders = document.querySelectorAll('.package-header');

    packageHeaders.forEach(header => {
        header.style.display = '';
    });
}

// Hide mobile swiper when in restricted state (only one package)
function hideMobileSwiper() {
    const mobileSwiper = document.querySelector('.package-slider-mobile');
    if (mobileSwiper) {
        mobileSwiper.style.display = 'none';
    }
}

// Show mobile swiper when not in restricted state
function showMobileSwiper() {
    const mobileSwiper = document.querySelector('.package-slider-mobile');
    if (mobileSwiper) {
        mobileSwiper.style.display = '';
    }
}

// Hide progress step 3 when in restricted state
function hideProgressStep3() {
    const progressStep3 = document.querySelector('[data-step="3"]');
    if (progressStep3) {
        progressStep3.style.display = 'none';
    }
}

// Show progress step 3 when not in restricted state
function showProgressStep3() {
    const progressStep3 = document.querySelector('[data-step="3"]');
    if (progressStep3) {
        progressStep3.style.display = '';
    }
}

// Check if the current appointment is in a restricted state
function checkIfRestrictedState() {
    // Get appointment data from session storage
    const appointmentData = loadSelectedAppointmentFromSession();

    if (appointmentData &&
        appointmentData.screening &&
        appointmentData.screening.state &&
        window.RESTRICTED_STATES &&
        window.RESTRICTED_STATES.includes(appointmentData.screening.state)) {
        return true;
    }
    return false;
}

// Check if date of birth step is enabled
function checkIfDobEnabled() {
    // Check if DOB container exists in the DOM
    const dobContainer = document.querySelector('.date-of-birth-container');
    return dobContainer !== null;
}
